import { Box, Paper, Typography, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { knownDevices, ipInSubnet } from '../data/networkData';
import { mockSecurityData } from './DeviceDetail';

// ── Build deduplicated device list grouped by hostname ─────────

function buildDeviceList() {
  const map = {};
  Object.entries(knownDevices).forEach(([ip, d]) => {
    if (!map[d.hostname]) map[d.hostname] = { ...d, ips: [] };
    map[d.hostname].ips.push(ip);
  });
  return Object.values(map);
}

const allDevices = buildDeviceList();

function purposeCategory(purpose = '') {
  if (purpose.includes('Switch'))                                   return 'Switches';
  if (purpose.includes('Server') || purpose.includes('Controller')) return 'Servers';
  if (purpose.includes('VM') || purpose.includes('Hypervisor'))     return 'Virtualization';
  if (purpose.includes('Scanner'))                                  return 'Security Tools';
  if (purpose.includes('Workstation'))                              return 'Workstations';
  return 'Other';
}

function filterDevices(group) {
  switch (group.type) {
    case 'os':       return allDevices.filter(d => d.os === group.value);
    case 'type':     return allDevices.filter(d => d.type === group.value);
    case 'category': return allDevices.filter(d => purposeCategory(d.purpose) === group.value);
    case 'purpose':  return allDevices.filter(d => d.purpose && d.purpose.toLowerCase().startsWith(group.value.toLowerCase()));
    case 'vlan':     return allDevices.filter(d => d.ips.some(ip => ipInSubnet(ip.split(' ')[0], group.value)));
    default:         return allDevices;
  }
}

// ── Device Card ────────────────────────────────────────────────

function DeviceCard({ device, onSelectDevice }) {
  const mock = mockSecurityData(device.hostname);
  const isOffline = device.status === 'offline';
  const riskColor = mock.riskScore >= 85 ? '#4caf50' : mock.riskScore >= 70 ? '#ff9800' : '#f44336';

  return (
    <Paper
      onClick={() => onSelectDevice(device)}
      sx={{
        backgroundColor: '#111827',
        border: '1px solid #1e3a5f',
        borderRadius: 2,
        padding: '16px 20px',
        cursor: 'pointer',
        transition: 'border-color 0.15s',
        '&:hover': { borderColor: '#00bcd4' },
      }}
    >
      {/* Header — hostname + risk score */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 1.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FiberManualRecordIcon sx={{ fontSize: 11, color: isOffline ? '#f44336' : '#4caf50' }} />
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 18, fontFamily: 'monospace' }}>
            {device.hostname}
          </Typography>
        </Box>
        <Typography sx={{ color: riskColor, fontSize: 42, fontWeight: 700, lineHeight: 1 }}>
          {mock.riskScore}
        </Typography>
      </Box>

      {/* IPs */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginBottom: 2 }}>
        {device.ips.map(ip => (
          <Typography key={ip} sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 14, backgroundColor: '#0a1525', padding: '3px 8px', borderRadius: 1 }}>
            {ip}
          </Typography>
        ))}
      </Box>

      {/* Details — Purpose first, full-width stack */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, marginBottom: 2 }}>
        {[
          { label: 'Purpose', value: device.purpose },
          { label: 'OS',      value: device.os },
          { label: 'Type',    value: device.type },
          { label: 'AV',      value: mock.avDaysAgo < 2 ? 'Current' : 'Outdated', color: mock.avDaysAgo < 2 ? '#4caf50' : '#f44336' },
        ].map(({ label, value, color }) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px dotted #1e3a5f', paddingBottom: 0.75 }}>
            <Typography sx={{ color: '#4a5a70', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
            <Typography sx={{ color: color || '#c0cfe0', fontSize: 16, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value || '—'}</Typography>
          </Box>
        ))}
      </Box>

      {/* Vuln chips + STIG */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {[
          { label: 'C', count: mock.critical, color: '#f44336' },
          { label: 'H', count: mock.high,     color: '#ff9800' },
          { label: 'M', count: mock.medium,   color: '#ffeb3b' },
          { label: 'L', count: mock.low,      color: '#4caf50' },
        ].map(({ label, count, color }) => (
          <Chip key={label} label={`${label}: ${count}`} size="small" sx={{
            backgroundColor: count > 0 ? `${color}22` : '#0d1825',
            color: count > 0 ? color : '#2a3a50',
            fontWeight: 700, fontSize: 12, height: 22,
          }} />
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ color: mock.stigPct >= 85 ? '#4caf50' : mock.stigPct >= 70 ? '#ff9800' : '#f44336', fontSize: 14, fontWeight: 700 }}>
          STIG {mock.stigPct}%
        </Typography>
      </Box>
    </Paper>
  );
}

// ── Group Summary Stats ────────────────────────────────────────

function StatBox({ label, value, color }) {
  return (
    <Box sx={{ textAlign: 'center', padding: '12px 20px', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2 }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 32, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Box>
  );
}

// ── Asset Group Page ───────────────────────────────────────────

export default function AssetGroup({ systemId, group, onBack, onSelectDevice }) {
  const devices = filterDevices(group);
  const online  = devices.filter(d => d.status !== 'offline').length;
  const offline = devices.filter(d => d.status === 'offline').length;
  const avgRisk = devices.length
    ? Math.round(devices.reduce((sum, d) => sum + mockSecurityData(d.hostname).riskScore, 0) / devices.length)
    : 0;
  const riskColor = avgRisk >= 85 ? '#4caf50' : avgRisk >= 70 ? '#ff9800' : '#f44336';
  const totalVulns = devices.reduce((sum, d) => {
    const m = mockSecurityData(d.hostname);
    return sum + m.critical + m.high + m.medium + m.low;
  }, 0);

  const groupTypeLabel = {
    os: 'Operating System',
    type: 'Device Type',
    category: 'Category',
    purpose: 'Purpose',
    vlan: 'VLAN',
  }[group.type] || 'Group';

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Back */}
      <Box
        onClick={onBack}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, marginBottom: 2.5, cursor: 'pointer', color: '#7a8fa6', '&:hover': { color: '#00bcd4' } }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 14 }}>Back to Assets</Typography>
      </Box>

      {/* Header */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 0.5 }}>
          {groupTypeLabel}
        </Typography>
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>
          {group.label}
        </Typography>
      </Box>

      {/* Summary stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, marginBottom: 3 }}>
        <StatBox label="Total Devices" value={devices.length} />
        <StatBox label="Online"        value={online}         color="#4caf50" />
        <StatBox label="Offline"       value={offline}        color={offline > 0 ? '#f44336' : '#4a5a70'} />
        <StatBox label="Avg Risk Score" value={avgRisk}       color={riskColor} />
      </Box>

      {/* Device cards grid */}
      {devices.length === 0 ? (
        <Typography sx={{ color: '#7a8fa6', fontSize: 14 }}>No devices found in this group.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {devices.map(device => (
            <DeviceCard key={device.hostname} device={device} onSelectDevice={onSelectDevice} />
          ))}
        </Box>
      )}

    </Box>
  );
}
